import React, { useState, useEffect } from 'react';
import { Card, Badge, Spinner, Alert, Button, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faClock, 
  faExclamationTriangle,
  faTimesCircle,
  faFileAlt,
  faSync,
  faEye,
  faCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import DocumentReviewModal from '../broker-portal/DocumentReviewModal';

interface BrokerReviewDocument {
  id: string;
  filename: string;
  original_filename: string;
  document_type: string;
  description: string;
  broker_review_status: string | null;
  broker_compliance_score: number | null;
  broker_reviewed_at: string | null;
  broker_review_notes: string | null;
  created_at: string;
  added_at: string;
  s3_key?: string;
  s3_url?: string;
  file_url?: string;
  transaction_id?: string;
}

interface BrokerReviewStatusProps {
  transactionId: string;
  refreshTrigger?: number; // Optional trigger to refresh data
  portal?: 'agent' | 'broker'; // Determines if we show review actions
  onDocumentReviewed?: () => void; // Callback when document is reviewed
}

const BrokerReviewStatus: React.FC<BrokerReviewStatusProps> = ({ 
  transactionId,
  refreshTrigger,
  portal = 'agent',
  onDocumentReviewed
}) => {
  const [documents, setDocuments] = useState<BrokerReviewDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<BrokerReviewDocument | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    fetchDocumentsWithStatus();
  }, [transactionId, refreshTrigger]);

  const fetchDocumentsWithStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching documents with broker review status...');
      console.log('📍 Transaction ID:', transactionId);
      console.log('🎭 Portal type:', portal);
      console.log('🔗 Fetching from:', `/broker-review/documents/${transactionId}`);
      
      const response = await axiosInstance.get(`/broker-review/documents/${transactionId}`);
      
      if (response.data.success && response.data.documents) {
        // Only show documents that have been submitted for review
        const reviewedDocs = response.data.documents.filter((doc: BrokerReviewDocument) => 
          doc.broker_review_status !== null
        );
        
        console.log(`📋 Found ${reviewedDocs.length} documents with broker review status`);
        console.log('📄 Document details:', reviewedDocs.map((d: BrokerReviewDocument) => ({
          filename: d.filename,
          status: d.broker_review_status,
          id: d.id,
          s3_key: d.s3_key,
          s3_url: (d as any).s3_url
        })));
        setDocuments(reviewedDocs);
      } else {
        setDocuments([]);
      }
    } catch (err: any) {
      console.error('❌ Error fetching broker review status:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        config: err.config
      });
      
      // More specific error messages
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (err.response?.status === 404) {
        setError('Review endpoint not found. Please refresh the page.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Unable to load document review status');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'pending_review':
        return (
          <Badge bg="warning" text="dark">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Pending Review
          </Badge>
        );
      case 'approved':
        return (
          <Badge bg="success">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Approved
          </Badge>
        );
      case 'needs_revision':
      case 'changes_requested':
        return (
          <Badge bg="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            Revision Needed
          </Badge>
        );
      case 'rejected':
        return (
          <Badge bg="dark">
            <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not reviewed yet';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleReviewClick = (document: BrokerReviewDocument) => {
    setSelectedDocument(document);
    setShowReviewModal(true);
  };

  const handleReviewComplete = () => {
    setShowReviewModal(false);
    setSelectedDocument(null);
    fetchDocumentsWithStatus(); // Refresh the document list
    if (onDocumentReviewed) {
      onDocumentReviewed();
    }
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Broker Review Status
          </h6>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <p className="mt-2 mb-0">Loading review status...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-3">
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Broker Review Status
          </h6>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (documents.length === 0) {
    return null; // Don't show the component if no documents are submitted for review
  }

  // Group documents by status
  const pendingDocs = documents.filter(d => d.broker_review_status === 'pending_review');
  const approvedDocs = documents.filter(d => d.broker_review_status === 'approved');
  const revisionDocs = documents.filter(d => 
    d.broker_review_status === 'needs_revision' || d.broker_review_status === 'changes_requested'
  );

  return (
    <>
    <Card className="mb-3 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Broker Review Status
          </h6>
          <div>
            <Badge bg="light" text="dark" className="me-2">
              {pendingDocs.length} Pending
            </Badge>
            <Badge bg="success" className="me-2">
              {approvedDocs.length} Approved
            </Badge>
            {revisionDocs.length > 0 && (
              <Badge bg="danger">
                {revisionDocs.length} Need Revision
              </Badge>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="list-group list-group-flush">
          {documents.map((doc) => (
            <div key={doc.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="mb-1">{doc.filename || doc.original_filename}</h6>
                  {doc.description && (
                    <small className="text-muted d-block">{doc.description}</small>
                  )}
                  <small className="text-muted">
                    Submitted: {formatDate(doc.added_at)}
                  </small>
                  {doc.broker_reviewed_at && (
                    <small className="text-muted d-block">
                      Reviewed: {formatDate(doc.broker_reviewed_at)}
                    </small>
                  )}
                </div>
                <div className="text-end">
                  {getStatusBadge(doc.broker_review_status)}
                  {doc.broker_compliance_score && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Score: {doc.broker_compliance_score}%
                      </small>
                    </div>
                  )}
                  {/* Show review button for brokers when document is pending */}
                  {portal === 'broker' && doc.broker_review_status === 'pending_review' && (
                    <div className="mt-2">
                      <ButtonGroup size="sm">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Review Document</Tooltip>}
                        >
                          <Button
                            variant="outline-primary"
                            onClick={() => handleReviewClick(doc)}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            Review
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Quick Approve</Tooltip>}
                        >
                          <Button
                            variant="outline-success"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to approve this document?')) {
                                try {
                                  const response = await axiosInstance.post('/broker-review/review-document', {
                                    document_id: doc.id,
                                    review_status: 'approved',
                                    review_notes: 'Quick approved from transaction view'
                                  });
                                  if (response.data.success) {
                                    fetchDocumentsWithStatus();
                                    if (onDocumentReviewed) onDocumentReviewed();
                                  }
                                } catch (error) {
                                  console.error('Error quick approving:', error);
                                }
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </Button>
                        </OverlayTrigger>
                      </ButtonGroup>
                    </div>
                  )}
                </div>
              </div>
              {doc.broker_review_notes && (
                <div className={`mt-2 p-2 rounded ${
                  doc.broker_review_status === 'needs_revision' || 
                  doc.broker_review_status === 'changes_requested' || 
                  doc.broker_review_status === 'rejected' 
                    ? 'bg-danger bg-opacity-10 border border-danger' 
                    : 'bg-light'
                }`}>
                  <small className={
                    doc.broker_review_status === 'needs_revision' || 
                    doc.broker_review_status === 'changes_requested' || 
                    doc.broker_review_status === 'rejected' 
                      ? 'text-danger' 
                      : 'text-muted'
                  }>
                    <strong>
                      {doc.broker_review_status === 'needs_revision' || 
                       doc.broker_review_status === 'changes_requested' 
                        ? '⚠️ Revision Required: ' 
                        : doc.broker_review_status === 'rejected'
                        ? '❌ Rejection Reason: '
                        : 'Broker Notes: '}
                    </strong>
                    {doc.broker_review_notes}
                  </small>
                  {(doc.broker_review_status === 'needs_revision' || 
                    doc.broker_review_status === 'changes_requested') && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <em>Please use the "Replace" button in the checklist above to upload a revised document.</em>
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card.Body>
      <Card.Footer className="bg-light text-center">
        <button 
          className="btn btn-link btn-sm text-decoration-none"
          onClick={fetchDocumentsWithStatus}
        >
          <FontAwesomeIcon icon={faSync} className="me-1" />
          Refresh Status
        </button>
      </Card.Footer>
    </Card>
    
    {/* Document Review Modal - only shown in broker portal */}
    {portal === 'broker' && selectedDocument && showReviewModal && (
      <DocumentReviewModal
        show={showReviewModal}
        onHide={() => {
          setShowReviewModal(false);
          setSelectedDocument(null);
        }}
        document={{
          id: selectedDocument.id,
          filename: selectedDocument.filename || selectedDocument.original_filename,
          document_type: selectedDocument.document_type,
          description: selectedDocument.description,
          s3_key: selectedDocument.s3_key,
          file_url: selectedDocument.file_url || selectedDocument.s3_url,
          transaction_id: selectedDocument.transaction_id || transactionId,
          broker_review_status: selectedDocument.broker_review_status,
          broker_compliance_score: selectedDocument.broker_compliance_score,
          broker_reviewed_at: selectedDocument.broker_reviewed_at,
          broker_review_notes: selectedDocument.broker_review_notes
        }}
        onReviewComplete={handleReviewComplete}
      />
    )}
  </>
  );
};

export default BrokerReviewStatus;